import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/api";
import { toast } from "react-hot-toast";
import { MdVisibility, MdVisibilityOff } from "react-icons/md";

const inputCls =
  "h-10 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none transition focus:border-[#051948] focus:ring-4 focus:ring-[#051948]/10 disabled:bg-slate-50 disabled:text-slate-400 disabled:cursor-not-allowed";
const btnPrimary =
  "h-9 px-4 rounded-lg bg-[#051948] text-sm font-medium text-white transition hover:bg-[#051948]/90 disabled:opacity-40 disabled:cursor-not-allowed";
const btnSecondary =
  "h-9 px-4 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-700 transition hover:border-[#051948]/30 hover:bg-[#051948]/5 hover:text-[#051948]";

const Field = ({ label, hint, error, children }) => (
  <div>
    <label className="mb-1.5 block text-sm font-medium text-slate-900">
      {label}
    </label>
    {children}
    {error ? (
      <p className="mt-1.5 text-xs text-red-600">{error}</p>
    ) : (
      hint && <p className="mt-1.5 text-xs text-slate-500">{hint}</p>
    )}
  </div>
);

const PasswordField = ({ label, hint, error, show, onToggle, ...props }) => (
  <Field label={label} hint={hint} error={error}>
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className={`${inputCls} pr-10`}
        {...props}
      />
      <button
        type="button"
        onClick={onToggle}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-[#051948]"
      >
        {show ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}
      </button>
    </div>
  </Field>
);

const Card = ({ title, description, children, footer }) => (
  <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div className="p-6">
      <h2 className="text-base font-semibold text-[#051948]">{title}</h2>
      <p className="mt-1 mb-6 text-sm text-slate-500">{description}</p>
      {children}
    </div>
    {footer && (
      <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-[#051948]/[0.03] px-6 py-3">
        {footer}
      </div>
    )}
  </section>
);

const Settings = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  const [userForm, setUserForm] = useState({
    name: "",
    email: "",
    phone: "",
  });

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [showPw, setShowPw] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const togglePw = (k) => setShowPw((s) => ({ ...s, [k]: !s[k] }));

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/auth/verify-token");
      setUser(res.data.user);
      setUserForm({
        name: res.data.user.name || "",
        email: res.data.user.email || "",
        phone: res.data.user.phone || "",
      });
    } catch (error) {
      console.error(error);
      toast.error("Failed to load user data");
    } finally {
      setLoading(false);
    }
  };

  const handleUserFormChange = (e) => {
    setUserForm({
      ...userForm,
      [e.target.name]: e.target.value,
    });
  };

  const handlePasswordFormChange = (e) => {
    setPasswordForm({
      ...passwordForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleUpdateInfo = async (e) => {
    e.preventDefault();
    try {
      setUpdating(true);
      await axiosInstance.put(`/auth/user/${user.id}`, userForm);
      toast.success("Profile updated successfully");
      fetchUserData();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    try {
      setChangingPassword(true);
      await axiosInstance.put("/auth/change-password", {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      toast.success("Password changed successfully");
      setPasswordForm({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowPasswordForm(false);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const isDirty =
    !!user &&
    (userForm.name !== (user.name || "") ||
      userForm.phone !== (user.phone || ""));

  const initials = (user?.name || "U")
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const pw = passwordForm.newPassword;
  const strength = [
    pw.length >= 6,
    pw.length >= 10,
    /[A-Z]/.test(pw) && /[a-z]/.test(pw),
    /\d/.test(pw) && /[^A-Za-z0-9]/.test(pw),
  ].filter(Boolean).length;
  const mismatch =
    passwordForm.confirmPassword && pw !== passwordForm.confirmPassword;

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-80 rounded-xl border border-slate-200 bg-white" />
      </div>
    );
  }

  return (
    <div className="px-6">
      {/* Profile */}
      <form onSubmit={handleUpdateInfo}>
        <Card
          title="Profile"
          description="Your personal information."
          footer={
            <>
              <span
                className={`text-xs ${
                  isDirty ? "font-medium text-[#051948]" : "text-slate-500"
                }`}
              >
                {isDirty ? "You have unsaved changes" : "All changes saved"}
              </span>
              <button
                type="submit"
                disabled={updating || !isDirty}
                className={btnPrimary}
              >
                {updating ? "Saving..." : "Save changes"}
              </button>
            </>
          }
        >
          <div className="mb-6 flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[#051948] text-lg font-semibold text-white">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-slate-900">
                {user?.name}
              </p>
              <p className="truncate text-sm text-slate-500">{user?.email}</p>
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <Field label="Name">
              <input
                type="text"
                name="name"
                value={userForm.name}
                onChange={handleUserFormChange}
                className={inputCls}
                required
              />
            </Field>
            <Field label="Phone">
              <input
                type="tel"
                name="phone"
                value={userForm.phone}
                onChange={handleUserFormChange}
                className={inputCls}
                placeholder="+212 ..."
              />
            </Field>
            <div className="sm:col-span-2">
              <Field label="Email" hint="Email cannot be changed.">
                <input
                  type="email"
                  value={userForm.email}
                  className={inputCls}
                  disabled
                  readOnly
                />
              </Field>
            </div>
          </div>
        </Card>
      </form>

      {/* Password */}
      {!showPasswordForm ? (
        <Card
          title="Password"
          description="Use a strong password you don't use elsewhere."
        >
          <button
            onClick={() => setShowPasswordForm(true)}
            className={btnSecondary}
          >
            Change password
          </button>
        </Card>
      ) : (
        <form onSubmit={handleChangePassword}>
          <Card
            title="Password"
            description="Use a strong password you don't use elsewhere."
            footer={
              <>
                <button
                  type="button"
                  className={btnSecondary}
                  onClick={() => {
                    setShowPasswordForm(false);
                    setPasswordForm({
                      currentPassword: "",
                      newPassword: "",
                      confirmPassword: "",
                    });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className={btnPrimary}
                >
                  {changingPassword ? "Updating..." : "Update password"}
                </button>
              </>
            }
          >
            <div className="max-w-md space-y-5">
              <PasswordField
                label="Current password"
                name="currentPassword"
                value={passwordForm.currentPassword}
                onChange={handlePasswordFormChange}
                show={showPw.current}
                onToggle={() => togglePw("current")}
                required
              />
              <div>
                <PasswordField
                  label="New password"
                  hint="At least 6 characters."
                  name="newPassword"
                  value={passwordForm.newPassword}
                  onChange={handlePasswordFormChange}
                  show={showPw.next}
                  onToggle={() => togglePw("next")}
                  required
                />
                {pw && (
                  <div className="mt-2 flex gap-1">
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${
                          i <= strength
                            ? strength <= 1
                              ? "bg-red-500"
                              : strength <= 2
                              ? "bg-[#051948]/50"
                              : "bg-[#051948]"
                            : "bg-slate-200"
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
              <PasswordField
                label="Confirm new password"
                name="confirmPassword"
                value={passwordForm.confirmPassword}
                onChange={handlePasswordFormChange}
                show={showPw.confirm}
                onToggle={() => togglePw("confirm")}
                error={mismatch ? "Passwords do not match" : ""}
                required
              />
            </div>
          </Card>
        </form>
      )}
    </div>
  );
};

export default Settings;